import type { Step } from "react-joyride";

/**
 * Helper: target a specific DataGrid column header by field name.
 * MUI X DataGrid renders headers as `[data-field="fieldName"]` inside
 * `.MuiDataGrid-columnHeaders`.
 */
const col = (field: string) =>
  `.MuiDataGrid-columnHeaders [data-field="${field}"]`;

/* ------------------------------------------------------------------ */
/*  Tour: Dashboard                                                    */
/* ------------------------------------------------------------------ */
export const dashboardSteps: Step[] = [
  {
    target: '[data-tour="page-title-dashboard"]',
    content:
      "Welcome to Squad Finance. This dashboard is your snapshot of the active reconciliation cycle — what reached QuickBooks, what's still in flight, and what needs your attention.",
    skipBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="cycle-selector"]',
    content:
      "The Global Cycle Filter controls every page. Pick a specific month or 'All Cycles' to see everything. The selection is sticky as you navigate.",
    placement: "bottom",
  },
  {
    target: '[data-tour="nav-reconciliation"]',
    content:
      "The Reconciliation section holds the day-to-day workflow pages: Transactions, Invoices, Match Review, File Issues, Duplicates, Invoice Gaps. Each one respects the cycle filter above.",
    placement: "right",
  },
  {
    target: '[data-tour="nav-system"]',
    content:
      "The System section has operational tools: Agent Status (3D office), QBD Queue (every QBXML command sent), Pipeline Errors, Rules Engine, Audit Trail, and Settings.",
    placement: "right",
  },
  {
    target: '[data-tour="hero-card"]',
    content:
      "The hero card shows the active cycle's reconciliation rate. The chips break it down: Total transactions, Matched (paired with an invoice), In Review, Unmatched, and Posted to QB.",
    placement: "bottom",
  },
  {
    target: '[data-tour="card-money-flow"]',
    content:
      "Money flow — the operational lifecycle in dollars. Sent to QBD, Waiting (matched / review / pending), and Failed sum to the cycle total. Below that, Auto-flow shows the class total: every row governed by Michelle's no-invoice account rules (9167, 9170, 9183, 9196, 7175) with a progress bar of the share already in QBD.",
    placement: "top",
  },
  {
    target: '[data-tour="card-type-breakdown"]',
    content:
      "Posted by type — donut + ranked legend of dollar volume per QuickBooks Desktop document type already in QBD. Colours match the tabs on the Transactions page so the visual vocabulary stays consistent end-to-end.",
    placement: "top",
  },
  {
    target: '[data-tour="card-insights"]',
    content:
      "Insights rotates every 7 seconds through the cycle's surprises: biggest single transaction, most-used category, auto-flow savings, top vendor, reconciliation rate, QBD progress. Hover to pause, arrows step manually.",
    placement: "top",
  },
  {
    target: '[data-tour="trend-chart"]',
    content:
      "Reconciliation Trend across cycles. Four series only (Matched, In Review, Unmatched, Posted to QB) — the legacy Exceptions line was retired when that status was merged into Unmatched.",
    placement: "bottom",
  },
  {
    target: '[data-tour="card-drift"]',
    content:
      "QB Drift Detection runs every 15 minutes — it cross-checks every transaction marked 'posted' against QBD reality via TxnQueryRq. If this card turns amber, a transaction disappeared from QB; investigate immediately.",
    placement: "top",
  },
  {
    target: '[data-tour="metric-cards"]',
    content:
      "Queue Depth = pending agent tasks. Pending Review = matches needing approval. Active Agents = should be 6/6. Cycle Status = active / completed / archived.",
    placement: "top",
  },
  {
    target: '[data-tour="agent-grid"]',
    content:
      "Each of the 6 AI agents — Statement Retrieval, Bank Import, QB Posting, Invoice Collector, Matching, Orchestrator — shows current status and counts. All idle + 6/6 Online = healthy system.",
    placement: "top",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: Transactions                                                 */
/* ------------------------------------------------------------------ */
export const transactionsSteps: Step[] = [
  {
    target: '[data-tour="page-title-transactions"]',
    content:
      "Transactions lists every line extracted from bank and credit card statements for the selected cycle.",
    skipBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="transactions-type-tabs"]',
    content:
      "Type tabs — eight slices through the same table by the QuickBooks Desktop document type each row landed as: Card Charges, Card Refunds, Checks, Deposits, Vendor Bills, Journal Entries, plus Not Posted (no QB row yet) and All Types. Each tab shows a live count. Filter is orthogonal to the Stage filter below.",
    placement: "bottom",
  },
  {
    target: '[data-tour="page-title-transactions-filters"]',
    content:
      "Filter bar: Stage (a single derived status that collapses the legacy match / posting / import / drift fields into one column), full-text search, and an amount range slider. Reset wipes them all in one click.",
    placement: "bottom",
  },
  {
    target: col("date"),
    content:
      "Date — the actual transaction date from the bank statement. Sortable. This date drives QB Desktop's <TxnDate> AND the cycle attribution (Jan transactions land in the Jan cycle regardless of when they were uploaded).",
    placement: "bottom",
  },
  {
    target: col("description"),
    content:
      "Description — the original text from the bank statement (e.g. 'AMAZON MKTPL*DI7G61HE3'). Use the search box to find specific transactions.",
    placement: "bottom",
  },
  {
    target: col("vendorName"),
    content:
      "Vendor — the cleaned/canonical merchant name. The vendor-cleaner rules collapse variants ('AMZN', 'AMAZON.COM', 'MKTPL*') into one canonical vendor.",
    placement: "bottom",
  },
  {
    target: col("amount"),
    content:
      "Amount — negative for debits, positive for credits, USD formatted.",
    placement: "bottom",
  },
  {
    target: col("stage"),
    content:
      "Stage — the headline column. One value per row that tells you what the row needs right now: NEW, AUTO-FLOW (no-invoice account, ready to post), NEEDS_INVOICE, READY, IN_QBD, DRIFT, DUPLICATE, FAILED, REVERSED. Click the chip when an action is available.",
    placement: "bottom",
  },
  {
    target: col("qbWhere"),
    content:
      "Where in QB — for rows already posted, a colored chip showing the QuickBooks document type. Click the '?' to open the How To Find modal with step-by-step navigation inside QuickBooks Desktop.",
    placement: "bottom",
  },
  {
    target: col("category"),
    content:
      "Category — the QuickBooks account assigned by the Rules Engine (e.g. 'Office Supplies', 'Bank Charges'). If no rule matched, Agent 3 picks the closest default and the Stage stays in pending_review until it's confirmed.",
    placement: "bottom",
  },
  {
    target: '.MuiDataGrid-virtualScroller',
    content:
      "Click any row to open the side-by-side document drawer: the bank statement on the left, the matched invoice on the right. Inline-edit fields with a reason — every change is recorded in the Audit Trail.",
    placement: "top",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: Invoices                                                     */
/* ------------------------------------------------------------------ */
export const invoicesSteps: Step[] = [
  {
    target: '[data-tour="page-title-invoices"]',
    content:
      "Invoices lists every receipt collected for the selected cycle — email attachments, Amazon orders, manual uploads, Teams drag-and-drop, vendor portals.",
    skipBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="page-title-invoices-filters"]',
    content:
      "Filter by Source, Match Status (Matched / Unmatched / Excluded), and the Extraction Status tabs (All / Extracted / Low Confidence / Failed / Pending).",
    placement: "bottom",
  },
  {
    target: col("vendorName"),
    content:
      "Vendor — extracted from the invoice PDF by OCR. May differ slightly from the bank statement vendor; the matching engine fuzzy-compares.",
    placement: "bottom",
  },
  {
    target: col("amount"),
    content:
      "Amount — the total from the invoice, used for amount matching against transactions (tolerance: $0.01).",
    placement: "bottom",
  },
  {
    target: col("date"),
    content:
      "Date — used for date-proximity matching (±5 days). If OCR couldn't extract a date, the invoice lands in File Issues for manual attribution.",
    placement: "bottom",
  },
  {
    target: col("invoiceNumber"),
    content:
      "Invoice # — order or reference number (Amazon order ID, eBay txn number). Secondary matching signal.",
    placement: "bottom",
  },
  {
    target: col("source"),
    content:
      "Source — where the invoice came from: Email, Amazon, Teams, Manual Upload, Vendor Portal.",
    placement: "bottom",
  },
  {
    target: col("ocrConfidence"),
    content:
      "Confidence — OCR certainty (0–100). Green (90+) = auto-acceptable. Yellow (75–89) = review recommended. Red (<50) = re-extract.",
    placement: "bottom",
  },
  {
    target: col("matchStatus"),
    content:
      "Match — 'matched' paired with a transaction, 'unmatched' no pair yet, 'excluded' manually removed.",
    placement: "bottom",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: Match Review                                                 */
/* ------------------------------------------------------------------ */
export const matchReviewSteps: Step[] = [
  {
    target: '[data-tour="page-title-matches"]',
    content:
      "Match Review is where you approve or reject transaction ↔ invoice pairs Agent 5 proposes. Each card shows both sides with a confidence breakdown.",
    skipBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="match-filters"]',
    content:
      "Filter by free-text (vendor / description / amount), Amount Match (Exact / Within $1 / Different), Confidence band (High ≥95 / Medium 75–94 / Low <75), and Status (Pending Review / Auto Matched / Confirmed / Rejected).",
    placement: "bottom",
  },
  {
    target: '[data-tour="match-card"]',
    content:
      "Each match card has two sides: TRANSACTION (left) and INVOICE (right). The confidence score sums Amount (40 pts) + Date (20 pts) + Vendor (30 pts) + Description (10 pts). Payment-rail transactions (Zelle, Venmo, ACH) get a vendor-gate override so 'Zelle' matched to 'BetterWay Property' doesn't fail on vendor mismatch.",
    placement: "bottom",
  },
  {
    target: '[data-tour="match-card"] .MuiCardContent-root > .MuiGrid-container > .MuiGrid-item:last-child .MuiStack-root:last-child',
    content:
      "Actions — Edit (inline editing requires a reason, logged in Audit). View Documents (PDF side-by-side). Approve (confirms + queues attachment for QB). Reject (sends transaction back to unmatched pool and blacklists the pair so it never resurfaces).",
    placement: "top",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: File Issues                                                  */
/* ------------------------------------------------------------------ */
export const fileReviewSteps: Step[] = [
  {
    target: '[data-tour="page-title-file-review"]',
    content:
      "File Issues lists files whose statement period couldn't be determined automatically. This queue intentionally ignores the global cycle filter — items here have no cycle yet.",
    skipBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="file-review-files"]',
    content:
      "Files table — File name, Source, Category, Attributed Date, Attribution method, Uploaded time, and an inline date picker to confirm. Agent 1 self-corrects automatically when parse succeeds: if a file's transactions clearly belong to a different month, the file gets re-attributed and dropped from this queue (audit stamp: post_parse_majority). Files only remain here when the parser couldn't extract enough dated rows to decide.",
    placement: "top",
  },
  {
    target: '[data-tour="file-review-invoices"]',
    content:
      "Invoices table — invoices whose date OCR failed. Click the eye to preview, pick the correct date, and Save — the invoice re-routes to the matching cycle automatically.",
    placement: "top",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: Invoice Gaps                                                 */
/* ------------------------------------------------------------------ */
export const invoiceGapsSteps: Step[] = [
  {
    target: '[data-tour="page-title-gaps"]',
    content:
      "Invoice Gap Detector lists bank transactions with no matching invoice — your follow-up worklist. Chase the receipt so Agent 5 can close the match. Rows on accounts marked invoiceRequired=false (9167, 9170, 9183, 9196, 7175) do not appear here because they're allowed to post without an invoice.",
    skipBeacon: true,
    placement: "bottom",
  },
  {
    target: col("date"),
    content:
      "Date — the transaction date. Use this when asking the vendor which purchase you mean.",
    placement: "bottom",
  },
  {
    target: col("description"),
    content:
      "Description — the original bank statement text. Often holds clues (order numbers, merchant codes) useful when requesting a receipt.",
    placement: "bottom",
  },
  {
    target: col("vendorName"),
    content:
      "Vendor — who to ask for the missing receipt. Copy into your follow-up email or Teams message.",
    placement: "bottom",
  },
  {
    target: col("amount"),
    content:
      "Amount — include this when requesting the receipt so the vendor can locate the document.",
    placement: "bottom",
  },
  {
    target: col("daysUnmatched"),
    content:
      "Days Unmatched — how long without an invoice. Sort descending to see the most urgent gaps first. After 48h the system can escalate via Teams.",
    placement: "bottom",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: QBD Queue                                                    */
/* ------------------------------------------------------------------ */
export const qbQueueSteps: Step[] = [
  {
    target: '[data-tour="page-title-qb-queue"]',
    content:
      "QBD Queue is the operator audit trail for everything the system tried to write to QuickBooks Desktop via the QBWC bridge.",
    skipBeacon: true,
    placement: "bottom",
  },
  {
    target: '[data-tour="qb-connection-card"]',
    content:
      "QBWC connection panel — last poll timestamp, paused state, the four operator controls (Pause new enqueues, Resume, Force-retry-all, Clear failures), and a fingerprint of the active company file. Watch the last-poll timestamp: if it drifts past a few minutes during business hours, the connector on the Altelix PC may need attention.",
    placement: "bottom",
  },
  {
    target: '[data-tour="qb-command-queue"]',
    content:
      "Every QBXML command ever enqueued: queued, sent, completed, failed, or expired. Click a row to expand the full request + response XML. When QB rejects a payload (code 0x80040400 'parse error'), the inspected XML is where you start debugging.",
    placement: "top",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: Duplicates                                                   */
/* ------------------------------------------------------------------ */
export const duplicatesSteps: Step[] = [
  {
    target: '[data-tour="page-title-duplicates"]',
    content:
      "Duplicates shows transactions the ingest pipeline flagged as likely duplicates of an existing row. Each entry pairs the new transaction with its candidate originals so you can confirm before they reach QuickBooks.",
    skipBeacon: true,
    placement: "bottom",
  },
  {
    target: '.MuiDataGrid-virtualScroller',
    content:
      "Click any row to open the side-by-side comparison drawer: the suspected duplicate on the left, the original(s) on the right. Confirm the duplicate to mark it blocked, or release it back into the flow if it was a false positive.",
    placement: "top",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: Review Queue                                                 */
/* ------------------------------------------------------------------ */
export const reviewQueueSteps: Step[] = [
  {
    target: '[data-tour="page-review-queue"]',
    content:
      "Review Queue holds transactions Agent 3 could not auto-post: it knows the amount and vendor but not which GL account to charge. Nothing here reaches QuickBooks until a human assigns the account and confirms.",
    skipBeacon: true,
    placement: "center",
  },
  {
    target: '[data-tour="page-review-queue-filters"]',
    content:
      "Filter the queue by Reason (vendor unknown, card mismatch, no GL in invoice, invoice unreadable), by Card, and by date range. Use these to batch similar rows — e.g. assign every 'vendor unknown' on one card in a single pass.",
    placement: "bottom",
  },
  {
    target: col("date"),
    content:
      "Date — the bank transaction date. Sorted newest-first by default; click to re-sort.",
    placement: "bottom",
  },
  {
    target: col("vendorName"),
    content:
      "Vendor — the cleaned merchant name. When this reads as an unknown vendor, that is usually why the row landed here: no catalog mapping to drive the GL.",
    placement: "bottom",
  },
  {
    target: col("amount"),
    content: "Amount — the value that will post to QuickBooks once you assign a GL.",
    placement: "bottom",
  },
  {
    target: col("vendorGlReviewReason"),
    content:
      "Reason — why the row needs review. Colour-coded: vendor unknown (amber), card mismatch (blue), no GL in invoice (purple), invoice unreadable (red). Add the vendor to the catalog and re-run from the Vendors page to clear whole groups at once.",
    placement: "bottom",
  },
  {
    target: ".MuiDataGrid-virtualScroller",
    content:
      "Click any row to open the source document — the bank statement or invoice that produced this line — so you can read it while deciding the GL. The review icon on the right opens the assignment dialog: pick a GL account (or create one), confirm the description, and send it to QuickBooks.",
    placement: "top",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: Vendors (admin)                                              */
/* ------------------------------------------------------------------ */
export const vendorsAdminSteps: Step[] = [
  {
    target: '[data-tour="page-vendors"]',
    content:
      "The Vendor catalog is the brain behind auto-categorization. Each vendor maps to a default GL account and card so Agent 3 can post without asking. Source-of-truth is docs/vendor-end-glaccont.md; edits here are runtime overrides until the next migration sync.",
    skipBeacon: true,
    placement: "center",
  },
  {
    target: '[data-tour="vendors-actions"]',
    content:
      "Add Vendor creates a new mapping. Re-evaluate Review Queue re-applies the current catalog to every transaction stuck in Review Queue — newly-mapped vendors get promoted and posted automatically, so you fix the catalog once and clear many rows.",
    placement: "bottom",
  },
  {
    target: '[data-tour="page-vendors-filters"]',
    content:
      "Search by name or notes, filter by the card a vendor defaults to, or by Type (Mapped / OCR-exception / Review-queue). Reset wipes them in one click.",
    placement: "bottom",
  },
  {
    target: col("name"),
    content:
      "Vendor Name — the canonical name the vendor-cleaner resolves bank descriptions to. This is the key the pipeline matches against.",
    placement: "bottom",
  },
  {
    target: col("defaultGlNumber"),
    content:
      "Default GL — the chart-of-accounts number a Mapped vendor's transactions post to automatically.",
    placement: "bottom",
  },
  {
    target: col("type"),
    content:
      "Type — Mapped (fixed GL + card, fully automatic), OCR-exception (GL read from the invoice OCR per transaction), or Review-queue (always routed to a human). This decides how much the vendor can be trusted to auto-post.",
    placement: "bottom",
  },
  {
    target: col("usageCount"),
    content:
      "Usage — how many transactions reference this vendor. High-usage vendors are the ones worth getting exactly right.",
    placement: "bottom",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: GL Accounts (admin)                                          */
/* ------------------------------------------------------------------ */
export const glAccountsAdminSteps: Step[] = [
  {
    target: '[data-tour="page-gl-accounts"]',
    content:
      "GL Accounts is the chart of accounts mirrored into Squad Finance. These are the accounts vendors and the Review Queue assign transactions to, and they sync to QuickBooks Desktop so the names match on both sides.",
    skipBeacon: true,
    placement: "center",
  },
  {
    target: '[data-tour="page-gl-accounts-filters"]',
    content:
      "Search by GL number or description, and filter by Account Type — Expense, Income, Asset, or Liability.",
    placement: "bottom",
  },
  {
    target: col("number"),
    content:
      "GL Number — the 4-digit account number (e.g. 6010). This is what posts on the QuickBooks transaction.",
    placement: "bottom",
  },
  {
    target: col("description"),
    content:
      "Description — the human-readable account name. Editing it propagates to QuickBooks via AccountMod, so only change it when the account's meaning truly changed; otherwise create a new GL.",
    placement: "bottom",
  },
  {
    target: col("qbSyncStatus"),
    content:
      "QBD Sync — whether this account exists in QuickBooks Desktop yet. Synced (green) is healthy; Sync failed (red) is clickable to retry the AccountAdd/AccountMod.",
    placement: "bottom",
  },
  {
    target: col("vendorRefCount"),
    content:
      "Vendors using / Txn Refs — how many vendors default to this account and how many transactions already reference it. A GL with references cannot be deleted until they're reassigned.",
    placement: "bottom",
  },
];

/* ------------------------------------------------------------------ */
/*  Tour: Cards (admin)                                                */
/* ------------------------------------------------------------------ */
export const cardsAdminSteps: Step[] = [
  {
    target: '[data-tour="page-cards"]',
    content:
      "Cards lists the six credit cards Altelix transactions are charged to. The set is fixed — cards are seeded at setup, so you can rename a card's description but not add or remove one.",
    skipBeacon: true,
    placement: "center",
  },
  {
    target: col("number"),
    content:
      "Card Number — the last four digits used to attribute each transaction to the right card and, in turn, the right QuickBooks sub-account.",
    placement: "bottom",
  },
  {
    target: col("description"),
    content:
      "Description — the friendly label for the card (e.g. who holds it or what it's used for). Click the edit pencil on a row to change it; the change is logged in the Audit Trail.",
    placement: "bottom",
  },
];

/** Map of route path → tour steps. Routes that aren't in this map
 * simply have no tour available (the "Start Tour" button becomes a
 * no-op while you're on them, which is correct). */
export const TOUR_STEPS: Record<string, Step[]> = {
  "/": dashboardSteps,
  "/transactions": transactionsSteps,
  "/invoices": invoicesSteps,
  "/matches": matchReviewSteps,
  "/pending-review": fileReviewSteps,
  "/gaps": invoiceGapsSteps,
  "/qb-queue": qbQueueSteps,
  "/duplicates": duplicatesSteps,
  "/review-queue": reviewQueueSteps,
  "/admin/vendors": vendorsAdminSteps,
  "/admin/gl-accounts": glAccountsAdminSteps,
  "/admin/cards": cardsAdminSteps,
  // `/exceptions` route was retired (v3.0.41-43) — Exceptions status
  // merged into Unmatched; the path redirects to /matches via routes.tsx.
};
